import React, {
  createContext,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
  useEffect,
} from 'react'
import { defaultCriteria, HallUser, SpeakingRole, Winner, WinType } from '@db8/shared'
import { usePollPubSub } from '@/hooks/pub-sub/use-poll-pub-sub'
import {
  SimplePoll,
  EvaluationPoll,
  PollResultsType,
} from '@/components/debateTools/polling/pollTypes'
import { useHall } from './HallProvider'
import { useHallUsers } from './HallUsersProvider'
import { useMeetingAppContext } from '@/MeetingAppContextDef'

type showWinnerType = {
  val: boolean
  initialized: boolean
}
export type PollContextType = {
  pollResults: PollResultsType
  setPollResults: Dispatch<SetStateAction<PollResultsType>>
  updateEvaluationPoll: (
    role: SpeakingRole.Affirmative | SpeakingRole.Negative,
    criterion: string,
    score: number,
  ) => void
  submitEvaluationPoll: (localRatings: EvaluationPoll) => void
  vote: (
    user: any,
    team: SpeakingRole.Affirmative | SpeakingRole.Negative,
    reasoning?: string,
  ) => void
  hasEveryoneVoted: boolean
  determineWinner: () => void
  showWinner: showWinnerType
  setShowWinner: Dispatch<SetStateAction<showWinnerType>>
  winningTeam: String
  resetPoll: () => void
}

const PollContext = createContext<PollContextType | undefined>(undefined)

export const PollProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hall, setHallMetadata, hallMetadata, ballots } = useHall()
  const { spectatorUsersSansBot, hallUsers, moderatorUsersWithOwner } = useHallUsers()
  const { setDebateToolMode } = useMeetingAppContext()
  const [showWinner, setShowWinner] = useState({
    val: false,
    initialized: false,
  })

  const createInitialEvaluationPoll = (criteria: string[]): EvaluationPoll<string> => ({
    [SpeakingRole.Affirmative]: criteria.reduce(
      (acc, criterion) => {
        acc[criterion] = 0
        return acc
      },
      {} as Record<string, number>,
    ),
    [SpeakingRole.Negative]: criteria.reduce(
      (acc, criterion) => {
        acc[criterion] = 0
        return acc
      },
      {} as Record<string, number>,
    ),
    castVotes: 0,
  })
  const [pollResults, setPollResults] = useState<PollResultsType>(() =>
    createInitialEvaluationPoll(hall?.evaluationCriteria || defaultCriteria),
  )

  const { broadCastVote } = usePollPubSub({
    onPollUpdate: (latestResults) => setPollResults(latestResults),
  })

  // * Watch hallMetadata to pubsub change to auto open on winner declaration, allow normal behavior afterwards.
  useEffect(() => {
    if (hallMetadata?.showWinner && !showWinner.initialized)
      setShowWinner({ val: true, initialized: true })
  }, [hallMetadata?.showWinner, showWinner.initialized])

  const updateEvaluationPoll = (
    role: SpeakingRole.Affirmative | SpeakingRole.Negative,
    criterion: string,
    score: number,
  ) => {
    setPollResults((prev) => {
      const updated = {
        ...prev,
        [role]: {
          ...prev[role],
          [criterion]: score,
        },
      }
      return updated
    })
  }

  const submitEvaluationPoll = (localRatings: EvaluationPoll<string>) => {
    setPollResults((prev) => {
      const prevEval = prev as EvaluationPoll

      const merged: EvaluationPoll = {
        [SpeakingRole.Affirmative]: { ...prevEval[SpeakingRole.Affirmative] },
        [SpeakingRole.Negative]: { ...prevEval[SpeakingRole.Negative] },
        castVotes: (prevEval.castVotes || 0) + 1,
      }

      ;([SpeakingRole.Affirmative, SpeakingRole.Negative] as const).forEach((role) => {
        for (const criterion in localRatings[role]) {
          merged[role][criterion] += localRatings[role][criterion]
        }
      })

      broadCastVote(merged)
      return merged
    })
  }

  const vote = (
    user: HallUser,
    teamType: SpeakingRole.Affirmative | SpeakingRole.Negative,
    reasoning: string,
  ) => {
    setPollResults((prev) => {
      const prevSimple = prev as SimplePoll
      const currentVotes = Array.isArray(prevSimple[teamType]) ? prevSimple[teamType] : []

      const newResults: SimplePoll = {
        ...prevSimple,
        [teamType]: [...currentVotes, { ...user, reasoning }],
        castVotes: prev.castVotes + 1 || 0,
      }
      broadCastVote(newResults)
      return newResults
    })
  }

  const getSimplePollVotes = (results: PollResultsType) => {
    const simpleResults = results as SimplePoll
    const affirmativeVotes = simpleResults[SpeakingRole.Affirmative]?.length || 0
    const negativeVotes = simpleResults[SpeakingRole.Negative]?.length || 0
    return {
      affirmativeVotes,
      negativeVotes,
      castVotes: affirmativeVotes + negativeVotes,
    }
  }

  const getEvaluationAverages = (results: PollResultsType) => {
    const evalResults = results as EvaluationPoll<string>
    const calculateAverage = (role: SpeakingRole) => {
      const criteria = Object.values(evalResults[role])
      const total: any = criteria.reduce((sum: number, val: number) => sum + val, 0)
      const average = criteria.length > 0 ? total / criteria.length : 0
      return average
    }

    return {
      affirmativeAvg: calculateAverage(SpeakingRole.Affirmative),
      negativeAvg: calculateAverage(SpeakingRole.Negative),
    }
  }

  const { affirmativeVotes, negativeVotes, castVotes } = useMemo(() => {
    if (!pollResults) return { affirmativeVotes: 0, negativeVotes: 0, castVotes: 0 }

    if (hall?.winType === WinType.SIMPLEPOLL) {
      return getSimplePollVotes(pollResults)
    }

    return {
      affirmativeVotes: 0,
      negativeVotes: 0,
      castVotes: pollResults.castVotes,
    }
  }, [pollResults, hall?.winType])

  const { affirmativeAvg, negativeAvg } = useMemo(() => {
    return hall?.winType === WinType.EVALUATIONPOLL
      ? getEvaluationAverages(pollResults)
      : { affirmativeAvg: 0, negativeAvg: 0 }
  }, [pollResults, hall?.winType])

  const hasEveryoneVoted = useMemo(() => {
    if (!hall) return false

    if ([WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL].includes(hall?.winType))
      return pollResults.castVotes > 0 && pollResults.castVotes >= spectatorUsersSansBot.length
    else if (
      hall?.winType === WinType.BALLOT &&
      ballots?.length > 0 &&
      ballots?.length === moderatorUsersWithOwner.length
    )
      return true

    return false
  }, [pollResults, spectatorUsersSansBot.length, hall?.winType, moderatorUsersWithOwner, ballots])

  const determineWinner = async () => {
    if (hall && !hasEveryoneVoted) return
    if (hallMetadata?.winningTeam !== Winner.Undecided) return

    let winningTeam
    if (hall.winType === WinType.SIMPLEPOLL) {
      if (affirmativeVotes > negativeVotes) winningTeam = Winner.Affirmative
      else if (negativeVotes > affirmativeVotes) winningTeam = Winner.Negative
      else if (negativeVotes === affirmativeVotes) winningTeam = Winner.Tie
    }

    if (hall.winType === WinType.EVALUATIONPOLL) {
      if (affirmativeAvg > negativeAvg) winningTeam = Winner.Affirmative
      else if (negativeAvg > affirmativeAvg) winningTeam = Winner.Negative
      else if (negativeAvg === affirmativeAvg) winningTeam = Winner.Tie
    }

    // * Calculate the averages across all feedback speaker points to determine a winner
    if (hall.winType === WinType.BALLOT) {
      try {
        const teamScores = {
          [SpeakingRole.Affirmative]: { totalPoints: 0, count: 0 },
          [SpeakingRole.Negative]: { totalPoints: 0, count: 0 },
        }

        ballots.forEach((ballot) => {
          ballot.feedbacks.forEach((fb) => {
            const team = hallUsers.find((u) => u.id === fb.hallUserId)?.speakingRole
            if (team === SpeakingRole.Affirmative || team === SpeakingRole.Negative) {
              teamScores[team].totalPoints += fb.points
              teamScores[team].count += 1
            }
          })
        })

        const avgAffirmative =
          teamScores[SpeakingRole.Affirmative].count > 0
            ? teamScores[SpeakingRole.Affirmative].totalPoints /
              teamScores[SpeakingRole.Affirmative].count
            : 0
        const avgNegative =
          teamScores[SpeakingRole.Negative].count > 0
            ? teamScores[SpeakingRole.Negative].totalPoints /
              teamScores[SpeakingRole.Negative].count
            : 0

        if (avgAffirmative > avgNegative) winningTeam = Winner.Affirmative
        else if (avgNegative > avgAffirmative) winningTeam = Winner.Negative
        else winningTeam = Winner.Tie
      } catch (error) {
      }
    }

    setDebateToolMode(null)
    setHallMetadata({
      debateComplete: true,
      showWinner: true,
      winningTeam,
      pinnedParticipants: [],
    })
  }

  const winningTeam = useMemo(() => {
    if (hallMetadata?.winningTeam === Winner.Affirmative) return hall?.affirmingTeamName
    else if (hallMetadata?.winningTeam === Winner.Negative) return hall?.dissentingTeamName
    else if (hallMetadata?.winningTeam === Winner.Tie) return 'This Round Ended in a Tie'
    else return ''
  }, [hallMetadata, hall])

  const resetPoll = () => {
    setShowWinner({
      val: false,
      initialized: false,
    })

    if (hall.winType === WinType.EVALUATIONPOLL) {
      const freshEvalPoll = {
        [SpeakingRole.Affirmative]: {},
        [SpeakingRole.Negative]: {},
        castVotes: 0,
      }

      for (const criterion of hall.evaluationCriteria || []) {
        freshEvalPoll[SpeakingRole.Affirmative][criterion] = 0
        freshEvalPoll[SpeakingRole.Negative][criterion] = 0
      }

      setPollResults(freshEvalPoll)
    }

    if (hall.winType === WinType.SIMPLEPOLL) {
      const freshSimplePoll = {
        [SpeakingRole.Affirmative]: [],
        [SpeakingRole.Negative]: [],
        castVotes: 0,
      }

      setPollResults(freshSimplePoll)
    }
  }
  return (
    <PollContext.Provider
      value={{
        pollResults,
        setPollResults: broadCastVote,
        updateEvaluationPoll,
        submitEvaluationPoll,
        vote,
        hasEveryoneVoted,
        determineWinner,
        showWinner,
        setShowWinner,
        winningTeam,
        resetPoll,
      }}
    >
      {children}
    </PollContext.Provider>
  )
}

export const usePoll = () => {
  const context = useContext(PollContext)
  if (!context) {
    throw new Error('usePoll must be used within a PollProvider')
  }
  return context
}
